import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { RollAdsModalForm } from "../roll-ads-modal-form";
import * as Styled from "./RollAdsModalBody.styled";
import { FileWithPreview, IMultiPartUploadType, MultiPartUploader } from "../../../../../libs";
import { adServerService, playlistsService, vodService } from "../../../../../services";
import { useAuth } from "../../../../../behavioral";
import { AdType, IAdServerRollAdRequestDto, IRollAdsModalBodyProps } from "../../../../../libs/models/ad-server";
import PlayListIcon from "../../../../../assets/images/icons/playlist.svg";
import { Loader } from "../../../../ui";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";


export const RollAdsModalBody: React.FC<IRollAdsModalBodyProps> = ({ campaignId, isEdit = false, onClose, fetchCampaigns }) => {
  const authInfo = useAuth();
  const [isVisible, setIsVisible] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState<FileWithPreview[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [options, setOptions] = useState<{ value: string; label: string; title: string }[]>([]);
  const [percentage, setPercentage] = React.useState(0);
  const [adsType, setAdsType] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchOptions = useCallback(async () => {
    try {
      const mediaItems = await vodService.getAllMediaItems();
      const playlists = await playlistsService.getAllPlaylists();

      // Transform media items (No Icon)
      const mediaOptions = mediaItems.map((item: any) => ({
        value: item.id,
        label: item.title,
        type: "media",
        title: item.title,
      }));

      // Transform playlists (Sort & Add Icon)
      const playlistOptions = playlists
        .sort((a: any, b: any) => a.title.localeCompare(b.title))
        .map((playlist: any) => ({
          value: playlist.playlistId,
          label: (
            <>
              <Styled.PlaylistIcon src={PlayListIcon} alt="Playlist Icon" />
              {playlist.title}
            </>
          ),
          title: playlist.title,
          type: "playlist",
        }));


      setOptions([...playlistOptions, ...mediaOptions]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const fetchCampaignData = useCallback(async () => {
    if (isEdit && campaignId) {
      setLoading(true);
      try {
        const response = await adServerService.getCampaignById(campaignId);
        const campaign = response?.campaign;
        formik.setValues({
          title: campaign?.campaignName || "",
          isVisible: campaign?.visibility || false,
          videoFiles: [
            ...(campaign?.postRollAds || []),
            ...(campaign?.preRollAds || []),
            ...(campaign?.midRollAds || []),
          ],
          displayOption: [
            ...(campaign?.targetPlaylists || []),
            ...(campaign?.targetVideos || []),
          ],
        });
      } catch (error) {
        let errorMessage = "An unexpected error occurred.";
        if (typeof error === "object" && error !== null && "message" in error) {
          errorMessage = String((error as { message: string }).message);
        }
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  }, [campaignId]);

  useEffect(() => {
    fetchCampaignData();
  }, [fetchCampaignData]);

  const resetForm = () => {
    formik.resetForm();
  };

  const createCampaign = React.useCallback(
    async (requestDto: IAdServerRollAdRequestDto) => {
      try {
        const response = await adServerService.createCampaigns(requestDto);
        toast.success(response?.message || "Campaign created successfully!");
      } catch (error: any) {
        toast.error(error?.message || "Failed to create campaign.");
      }
    },
    []
  );

  const MAX_FILE_SIZE_MB = 100;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const formik = useFormik({
    initialValues: {
      title: "",
      videoFiles: [] as FileWithPreview[],
      displayOption: [] as string[],
      isVisible: true,
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .trim()
        .required("Campaign Name is required")
        .test(
          "max-letters",
          "Campaign Name cannot exceed 100 alphabetic characters",
          (value) => {
            if (!value) return false;
            const alphabeticChars = value.replace(/[^a-zA-Z]/g, "");
            return alphabeticChars.length <= 100;
          }
        ),
      videoFiles: isEdit
        ? Yup.array().nullable()

          .test(
            "fileType",
            "Please select at least one ad type option",
            (files) =>
              !files ||
              files.every(
                (file) =>
                  !file?.file ||
                  ["Pre-roll", "Mid-roll", "Post-roll"].includes(file.fileType)
              )
          )
        :
        Yup.array()
          .nullable()
          .min(1, "Please upload an ad video before publishing")
          .test(
            "fileSize",
            `File size must be less than ${MAX_FILE_SIZE_MB}MB`,
            (files) => !files || files.every((file) => file.file.size <= MAX_FILE_SIZE_BYTES)
          )
          .test(
            "fileFormat",
            "Unsupported file format. Only MP4 and MOV are allowed.",
            (files) =>
              !files || files.every((file) =>
                ["video/mp4", "video/quicktime"].includes(file.file.type)
              )
          )
          .test(
            "fileType",
            "Please select at least one ad type option ",
            (files) =>
              !files ||
              files.every(
                (file) =>
                  ["Pre-roll", "Mid-roll", "Post-roll"].includes(file.fileType)
              )),
      displayOption: Yup.array().of(Yup.string()).min(1, "Please select at least one display option"),
    }),

    onSubmit: async (values, { resetForm }) => {
      let s3Responses: any[] = [];
      try {
        if (isEdit) {
          let fileAdsArray: any[] = [];
          let filteradsVideo: any[] = [];


          if (uploadedFiles && uploadedFiles.length > 0) {
            filteradsVideo = uploadedFiles.filter((videoFile) => videoFile.hasOwnProperty("file"));
          }

          if (values.videoFiles && values.videoFiles.length > 0) {
            values.videoFiles = values.videoFiles.filter((videoFile) => {
              if (videoFile.hasOwnProperty("file")) {
                return true;
              } else {
                fileAdsArray.push(videoFile);
                return false;
              }
            });

            if (values.videoFiles.length === 0) {
              values.videoFiles = [];
            }
          }

          if (values.videoFiles.length > 0) {
            const uploadPromises = values.videoFiles.map((video: any, index: number) => {
              return new Promise((resolve, reject) => {
                const uploader = new MultiPartUploader({
                  fileName: video?.file?.name,
                  file: video?.file,
                  clientId: authInfo?.user?.username,
                  mediaId: uuid(),
                  category: "video",
                  uploadType: IMultiPartUploadType.ADSERVER_VIDEO
                });

                uploader
                  .getOnProgress((params: any) => {
                    setPercentage(params.percentage);
                  })
                  .getOnComplete((params: any) => {
                    s3Responses.push({
                      fileName: video?.file?.name,
                      s3Key: params.s3Key,
                      adsType: filteradsVideo[index]?.fileType,
                      id: params.mediaId,
                    });

                    resolve(params);
                  })
                  .getOnError((error: any) => {
                    console.error("Upload error:", error);
                    toast.error("Error uploading video file");
                    reject(error);
                  });

                uploader.start();
              });
            });

            await Promise.all(uploadPromises);
          }
          const updatePayload = transformUpdatePayload(campaignId!, formik.values, uploadedFiles, s3Responses);
          const response = await adServerService.updateCampaigns(campaignId ?? undefined, updatePayload);
          toast.success(response?.data?.message || "Campaign updated successfully");
          fetchCampaigns();
        } else {
          const newCampaignId = uuid();
          if (values.videoFiles.length > 0) {
            const uploadPromises = values.videoFiles.map((video: any, index: number) => {
              return new Promise((resolve, reject) => {
                const uploader = new MultiPartUploader({
                  fileName: video?.file?.name,
                  file: video?.file,
                  clientId: authInfo?.user?.username,
                  mediaId: uuid(),
                  category: "video",
                  uploadType: IMultiPartUploadType.ADSERVER_VIDEO,
                  secondaryMediaId: newCampaignId,
                });

                uploader
                  .getOnProgress((params: any) => {
                    setPercentage(params.percentage);
                  })
                  .getOnComplete((params: any) => {
                    s3Responses.push({
                      fileName: video?.file?.name,
                      fileKey: params.s3Key,
                      adsType: uploadedFiles[index]?.fileType,
                      id: params.mediaId,
                    });

                    resolve(params);
                  })
                  .getOnError((error: any) => {
                    console.error("Upload error:", error);
                    toast.error("Error uploading video file");
                    reject(error);
                  });

                uploader.start();
              });
            });

            await Promise.all(uploadPromises);
            const req = transformData(values, s3Responses, newCampaignId);
            await createCampaign(req);
          }
          await fetchCampaigns();
        }

        resetForm();
        onClose();
      } catch (error) {
        console.error("Error during form submission:", error);
        toast.error("Something went wrong, please try again.");
      }
    }
  });

  function filterFiles(uploadedFiles, s3Responses, type) {
    const s3files = s3Responses.filter((file) => file.adsType?.toLowerCase() === type);
    const files = uploadedFiles.filter((file) => {
      const hasAllProps = file?.previewUrl && file?.file && file?.fileType;
      return file?.fileType?.toLowerCase() === type && !hasAllProps;
    });

    return [...s3files, ...files];
  }

  const transformUpdatePayload = (
    campaignId: string,
    formikValues: any,
    uploadedFiles: any[],
    s3Responses: any[],

  ) => {

    uploadedFiles.forEach(item => {
      if (!item?.fileType) {
        item.fileType = item?.adType;
      }
    });

    return {
      campaign: campaignId,
      campaignName: formikValues.title,
      visibility: formikValues.isVisible,
      targetPlaylists: formikValues.displayOption,
      preRollAds: filterFiles(uploadedFiles, s3Responses, AdType.PreRoll.toLowerCase()),
      midRollAds: filterFiles(uploadedFiles, s3Responses, AdType.MidRoll.toLowerCase()),
      postRollAds: filterFiles(uploadedFiles, s3Responses, AdType.PostRoll.toLowerCase()),
    };
  };

  function transformData(input: any, s3Responses: any[], campaignId: string): IAdServerRollAdRequestDto {
    const selectedOptions: string[] = input.displayOption || [];
    const targetPlaylists = options
      .filter((opt) => selectedOptions.includes(opt.value) && (opt as any).type === "playlist")
      .map((opt) => opt.value);

    const targetVideos = options
      .filter((opt) => selectedOptions.includes(opt.value) && (opt as any).type === "media")
      .map((opt) => opt.value);

    return {
      campaignId: campaignId,
      campaignName: input.title,
      visibility: input.isVisible,
      preRollAds: s3Responses
        .filter(file => file.adsType.toLowerCase() === AdType.PreRoll.toLowerCase())
        .map(file => ({
          fileName: file.fileName,
          s3Key: file.fileKey,
          id: file.id,
        })),
      midRollAds: s3Responses
        .filter(file => file.adsType.toLowerCase() === AdType.MidRoll.toLowerCase())
        .map(file => ({
          fileName: file.fileName,
          s3Key: file.fileKey,
          id: file.id,
        })),
      postRollAds: s3Responses
        .filter(file => file.adsType.toLowerCase() === AdType.PostRoll.toLowerCase())
        .map(file => ({
          fileName: file.fileName,
          s3Key: file.fileKey,
          id: file.id,
        })),
      targetPlaylists,
      targetVideos,
    };
  }

  const handleDrop = (acceptedFiles: FileWithPreview[]) => {
    const validFiles: FileWithPreview[] = [];
    const invalidFiles: string[] = [];

    acceptedFiles.forEach((file) => {
      const fileSizeMB = file.file.size / (1024 * 1024); // Convert size to MB
      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        invalidFiles.push(file.file.name);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      formik.setFieldError("videoFiles", `The following files are too large (max 20MB): ${invalidFiles.join(", ")}`);
    } else {
      formik.setFieldValue("videoFiles", [...uploadedFiles, ...validFiles]);
      const newFiles = [...uploadedFiles, ...validFiles];
      setUploadedFiles(newFiles);
      formik.setFieldValue("videoFiles", newFiles);
    }
  };

  const handleDeleteFile = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i != index);
    setUploadedFiles(newFiles);
    formik.setFieldValue("videoFiles", newFiles);
  };

  const handleVisibilityToggle = () => {
    setIsVisible(!isVisible);
    formik.setFieldValue("isVisible", !isVisible);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <RollAdsModalForm
          formik={formik}
          isEdit={isEdit}
          onDropHandler={(files) => handleDrop(files)}
          handleVisibilityToggle={handleVisibilityToggle}
          displayOptions={options.length > 0 ? options : []}
          uploadedFile={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          percentage={percentage}
          handleDeleteFile={handleDeleteFile}
          adsType={adsType}
          setAdsType={setAdsType}
          resetForm={resetForm}
          onClose={onClose}
          isDisabled={isEdit}
        />
      )}
    </>
  );
};

